import type * as React from 'react'

import {
  Badge,
  ChannelIcon,
  type ChannelType,
  Flex,
  Text,
  ToggleGroup,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import type { ChannelGroup_ChannelGroup } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'

import AppActionCreators from '../../action-creators/AppActionCreators'

import styles from './ChannelGroupItem.module.css'

const ChannelGroupsItem = ({
  channelGroup,
  isSelected,
}: {
  // eslint-disable-next-line camelcase
  channelGroup: ChannelGroup_ChannelGroup
  isSelected: boolean
}): JSX.Element => {
  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  const channelIds = channelGroup.channels?.map((channel) => channel.id) ?? []

  const handleSelectGroup = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    const dataState = event.currentTarget.dataset.state

    // Only track if the group is not selected before
    if (dataState === 'off' && accountId && organizationId) {
      BufferTracker.channelGroupSelected({
        accountId,
        channelGroupId: String(channelGroup.id),
        organizationId,
        userId: accountId,
      })
    }

    if (isSelected) {
      AppActionCreators.unselectProfiles(channelIds)
    } else {
      AppActionCreators.selectProfilesOnBehalfOfUser(channelIds)
    }
  }

  return (
    <Tooltip
      align="start"
      content={
        <Flex direction="column" gap="2xs">
          <Text size="sm" weight="medium">
            {channelGroup.name}
          </Text>
          {channelGroup.channels?.map((channel) => (
            <Flex
              direction="row"
              align="center"
              gap="xs"
              key={`${channelGroup.id}-${channel.id}`}
            >
              <ChannelIcon
                color="inverted"
                size="xsmall"
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                type={channel.service as ChannelType}
              />
              <Text size="sm">{channel.name}</Text>
            </Flex>
          ))}
        </Flex>
      }
    >
      <ToggleGroup.Item
        value={String(channelGroup.id)}
        className={styles.channelGroupItem}
        onClick={handleSelectGroup}
        data-state={isSelected ? 'on' : 'off'}
      >
        <span className={styles.name}>{channelGroup.name}</span>{' '}
        <Badge className={styles.count}>
          {channelGroup.channels?.length}
          <VisuallyHidden>
            {channelGroup.channels?.length === 1 ? 'channel' : 'channels'} in
            this group
          </VisuallyHidden>
        </Badge>
        <VisuallyHidden>
          {isSelected
            ? 'Click to unselect all channels in this group'
            : 'Click to select all channels in this group'}
        </VisuallyHidden>
      </ToggleGroup.Item>
    </Tooltip>
  )
}

export default ChannelGroupsItem
